<template>
  <div class="databases-container">
    <!-- 左侧数据表列表 -->
    <div class="database-sidebar">
      <div class="sidebar-header">
        <h3>数据表</h3>
        <div class="header-actions">
          <el-button v-if="$canWrite()" icon="el-icon-plus" circle size="small" @click="openCreateDialog"></el-button>
          <el-button icon="el-icon-refresh" circle size="small" @click="fetchTables"></el-button>
        </div>
      </div>
      
      <el-input
        placeholder="搜索表名"
        v-model="searchKeyword"
        prefix-icon="el-icon-search"
        size="small"
        class="search-input"
      ></el-input>

      <div class="table-list">
        <div 
          v-for="table in filteredTables" 
          :key="table.id"
          :class="['table-item', { active: selectedTable && selectedTable.id === table.id }]"
          @click="selectTable(table)"
        >
          <i class="el-icon-document"></i>
          <div class="table-info">
            <span class="table-name">{{ table.name }}</span>
            <span class="table-desc">{{ table.description || '暂无描述' }}</span>
          </div>
        </div>

        <div v-if="filteredTables.length === 0" class="empty-list">
          <p>暂无数据表</p>
        </div>
      </div>
    </div>

    <!-- 右侧内容区域 -->
    <div class="table-content">
      <!-- 空状态 -->
      <div v-if="!selectedTable" class="empty-state">
        <i class="el-icon-folder-opened"></i>
        <p v-if="$canWrite()">您还没有建数据表，点击创建一个吧</p>
        <p v-else>暂无数据表</p>
        <el-button v-if="$canWrite()" type="primary" @click="openCreateDialog">创建数据表</el-button>
      </div>

      <!-- 数据表详情 -->
      <div v-else class="table-detail">
        <div class="detail-header">
          <div class="header-left">
            <h2>{{ selectedTable.name }}</h2>
            <span class="table-desc-text">{{ selectedTable.description || '暂无描述' }}</span>
          </div>
          <div class="header-actions">
            <el-button v-if="$canWrite()" size="mini" icon="el-icon-edit" @click="openEditDialog">编辑</el-button>
            <el-button v-if="$canWrite()" size="mini" icon="el-icon-delete" type="danger" @click="handleDelete">删除</el-button>
          </div>
        </div>

        <el-tabs v-model="activeTab" class="table-tabs">
          <!-- 数据 -->
          <el-tab-pane label="数据" name="data">
            <div class="data-section">
              <!-- 操作栏：搜索和按钮 -->
              <div class="data-toolbar">
                <el-input
                  v-model="dataSearchKeyword"
                  placeholder="搜索数据"
                  size="small"
                  prefix-icon="el-icon-search"
                  clearable
                  style="width: 300px;"
                  @clear="dataSearchKeyword = ''"
                ></el-input>
                <span class="search-tip" v-if="filteredTableData.length !== tableData.length">
                  找到 {{ filteredTableData.length }} 条结果，共 {{ tableData.length }} 条数据
                </span>
                <div class="action-buttons" style="margin-left: auto;">
                  <el-button v-if="$canWrite()" size="mini" type="primary" icon="el-icon-plus" @click="openAddDataDialog">添加</el-button>
                  <el-dropdown @command="handleImportExport">
                    <el-button size="mini" icon="el-icon-s-operation">
                      导入/导出<i class="el-icon-arrow-down el-icon--right"></i>
                    </el-button>
                    <el-dropdown-menu slot="dropdown">
                      <el-dropdown-item v-if="$canWrite()" command="import" icon="el-icon-upload2">导入数据</el-dropdown-item>
                      <el-dropdown-item command="export-json" icon="el-icon-download">导出为 JSON</el-dropdown-item>
                      <el-dropdown-item command="export-csv" icon="el-icon-download">导出为 CSV</el-dropdown-item>
                    </el-dropdown-menu>
                  </el-dropdown>
                  <el-button size="mini" icon="el-icon-refresh" @click="fetchTableData">刷新</el-button>
                </div>
              </div>
              
              <!-- 表格容器 -->
              <div class="table-wrapper">
                <el-table 
                  :data="filteredTableData" 
                  border 
                  style="width: 100%" 
                  height="calc(100vh - 450px)"
                  @selection-change="handleSelectionChange"
                >
                  <el-table-column v-if="$canWrite()" type="selection" width="55"></el-table-column>
                  <el-table-column 
                    v-for="field in tableFields" 
                    :key="field.name"
                    :prop="field.name" 
                    :label="field.name"
                    :width="field.name === 'id' ? 80 : 150"
                    show-overflow-tooltip
                  >
                    <template slot="header">
                      <el-tooltip :content="getFieldTooltip(field)" placement="top">
                        <span>{{ field.name }}</span>
                      </el-tooltip>
                    </template>
                  </el-table-column>
                  <el-table-column label="操作" width="150" fixed="right" v-if="$canWrite()">
                    <template slot-scope="scope">
                      <el-button type="text" size="small" @click="editData(scope.row)">编辑</el-button>
                      <el-button type="text" size="small" style="color: #F56C6C;" @click="deleteData(scope.row.id)">删除</el-button>
                    </template>
                  </el-table-column>
                </el-table>
                
                <div v-if="filteredTableData.length === 0 && tableData.length === 0" class="empty-data">
                  <p>暂无数据</p>
                </div>
                <div v-if="filteredTableData.length === 0 && tableData.length > 0" class="empty-data">
                  <p>没有找到匹配的数据</p>
                </div>
              </div>
              
              <!-- 分页固定在底部 -->
              <div class="pagination-footer">
                <div class="footer-left">
                  <el-button 
                    v-if="$canWrite()"
                    size="small" 
                    type="danger" 
                    icon="el-icon-delete"
                    :disabled="selectedRows.length === 0"
                    @click="batchDeleteData"
                  >
                    批量删除 ({{ selectedRows.length }})
                  </el-button>
                </div>
                
                <el-pagination
                  @current-change="handleDataPageChange"
                  :current-page="dataPagination.currentPage"
                  :page-size="dataPagination.pageSize"
                  layout="total, prev, pager, next, jumper"
                  :total="filteredDataTotal"
                ></el-pagination>
              </div>
            </div>
          </el-tab-pane>

          <!-- 表结构 -->
          <el-tab-pane label="表结构" name="structure">
            <div class="structure-section">
              <div class="section-header">
                <span>字段列表</span>
                <div class="action-buttons">
                  <el-button size="mini" type="success" icon="el-icon-download" @click="exportTableStructure">导出</el-button>
                  <el-button v-if="$canWrite()" size="mini" type="primary" @click="openBatchFieldDialog">添加字段</el-button>
                </div>
              </div>
              
              <div class="table-container">
                <el-table :data="tableFields" border style="width: 100%;" height="100%">
                  <el-table-column type="index" label="序号" width="60" fixed></el-table-column>
                  <el-table-column prop="name" label="字段名" min-width="150">
                    <template slot-scope="scope">
                      <el-tooltip :content="scope.row.comment || '无备注'" placement="top">
                        <span>{{ scope.row.name }}</span>
                      </el-tooltip>
                    </template>
                  </el-table-column>
                  <el-table-column prop="type" label="类型" width="120"></el-table-column>
                  <el-table-column prop="length" label="长度" width="100"></el-table-column>
                  <el-table-column prop="nullable" label="可空" width="80">
                    <template slot-scope="scope">
                      <el-tag :type="scope.row.nullable ? 'success' : 'info'" size="small">
                        {{ scope.row.nullable ? '是' : '否' }}
                      </el-tag>
                    </template>
                  </el-table-column>
                  <el-table-column prop="default" label="默认值" min-width="120"></el-table-column>
                  <el-table-column prop="comment" label="备注" min-width="150" show-overflow-tooltip></el-table-column>
                  <el-table-column label="操作" width="150" fixed="right" v-if="$canWrite()">
                    <template slot-scope="scope">
                      <el-button type="text" size="small" @click="editFieldInBatch(scope.row, scope.$index)">编辑</el-button>
                      <el-button 
                        type="text" 
                        size="small" 
                        style="color: #F56C6C;" 
                        @click="deleteField(scope.$index)"
                        :disabled="scope.row.isDefault"
                      >删除</el-button>
                    </template>
                  </el-table-column>
                </el-table>
              </div>
            </div>
          </el-tab-pane>
        </el-tabs>
      </div>
    </div>

    <!-- 创建/编辑数据表对话框 -->
    <el-dialog
      :title="dialogMode === 'create' ? '创建数据表' : '编辑数据表'"
      :visible.sync="tableDialogVisible"
      width="600px"
      :close-on-click-modal="false"
    >
      <!-- 创建模式切换（仅在创建时显示） -->
      <div v-if="dialogMode === 'create'" class="create-mode-switch">
        <el-radio-group v-model="createMode" size="small">
          <el-radio-button label="manual">手动创建</el-radio-button>
          <el-radio-button label="import">批量导入</el-radio-button>
        </el-radio-group>
      </div>

      <!-- 手动创建模式 -->
      <el-form v-if="createMode === 'manual'" ref="tableForm" :model="tableForm" :rules="tableRules" label-width="100px">
        <el-form-item label="表名" prop="name">
          <el-input v-model="tableForm.name" placeholder="请输入表名，例如: users"></el-input>
          <span class="form-tip">表名只能包含字母、数字和下划线</span>
        </el-form-item>
        <el-form-item label="表描述" prop="description">
          <el-input 
            v-model="tableForm.description" 
            type="textarea" 
            :rows="3"
            placeholder="请输入表的描述信息"
          ></el-input>
        </el-form-item>
        <el-form-item label="初始字段">
          <el-checkbox-group v-model="tableForm.initFields">
            <el-checkbox label="id">ID (主键)</el-checkbox>
            <el-checkbox label="created_at">创建时间</el-checkbox>
            <el-checkbox label="updated_at">更新时间</el-checkbox>
          </el-checkbox-group>
          <span class="form-tip">选择要自动创建的字段</span>
        </el-form-item>
      </el-form>

      <!-- 批量导入模式 -->
      <div v-if="createMode === 'import'" class="batch-import-section">
        <el-alert
          title="支持导入 JSON 文件批量创建数据表"
          type="info"
          :closable="false"
          show-icon
          style="margin-bottom: 20px;"
        >
          <div>上传 JSON 文件，系统会自动分析数据结构并创建表和字段</div>
        </el-alert>

        <el-upload
          ref="tableImportUpload"
          :auto-upload="false"
          :on-change="handleTableImportFileChange"
          :file-list="tableImportFileList"
          :limit="1"
          accept=".json"
          drag
        >
          <i class="el-icon-upload"></i>
          <div class="el-upload__text">将文件拖到此处，或<em>点击上传</em></div>
          <div class="el-upload__tip" slot="tip">支持 JSON 格式，文件大小不超过 10MB</div>
        </el-upload>

        <div v-if="tableImportPreview" class="import-preview" style="margin-top: 20px;">
          <el-divider content-position="left">预览信息</el-divider>
          <el-descriptions :column="2" border size="small">
            <el-descriptions-item label="表名">
              <el-input v-model="tableImportPreview.tableName" size="small" placeholder="请输入表名"></el-input>
            </el-descriptions-item>
            <el-descriptions-item label="数据条数">{{ tableImportPreview.dataCount }}</el-descriptions-item>
            <el-descriptions-item label="检测到的字段" :span="2">
              <el-tag v-for="field in tableImportPreview.fields" :key="field.name" size="small" style="margin-right: 5px; margin-bottom: 5px;">
                {{ field.name }} ({{ field.type }})
              </el-tag>
            </el-descriptions-item>
          </el-descriptions>
        </div>
      </div>

      <div slot="footer">
        <el-button @click="tableDialogVisible = false">取消</el-button>
        <el-button type="primary" @click="handleTableSubmit" :loading="loading">
          {{ dialogMode === 'create' ? (createMode === 'import' ? '导入并创建' : '创建') : '更新' }}
        </el-button>
      </div>
    </el-dialog>

    <!-- 添加字段抽屉 -->
    <el-drawer
      :title="editingFieldIndex >= 0 ? '编辑字段' : '添加字段'"
      :visible.sync="batchFieldDialogVisible"
      direction="rtl"
      size="60%"
      :close-on-press-escape="false"
      :wrapperClosable="false"
    >
      <div class="drawer-content">
        <div class="batch-field-section">
          <div class="batch-field-header">
            <span>字段列表</span>
            <div v-if="editingFieldIndex < 0">
              <el-button size="small" type="success" icon="el-icon-upload2" @click="showSqlImport = !showSqlImport">
                {{ showSqlImport ? '手动添加' : '导入 SQL' }}
              </el-button>
              <el-button size="small" type="primary" icon="el-icon-plus" @click="addBatchFieldRow" v-if="!showSqlImport">添加一行</el-button>
            </div>
          </div>

          <!-- SQL 导入区域 -->
          <div v-if="showSqlImport" class="sql-import-section">
            <el-alert
              title="支持导入 MySQL CREATE TABLE 语句"
              type="info"
              :closable="false"
              show-icon
            >
              <div>粘贴完整的 CREATE TABLE 语句，系统会自动解析字段结构</div>
            </el-alert>
            
            <el-input
              v-model="sqlStatement"
              type="textarea"
              :rows="12"
              placeholder="粘贴 MySQL 建表语句，例如：&#10;&#10;CREATE TABLE `users` (&#10;  `id` int(11) NOT NULL AUTO_INCREMENT,&#10;  `username` varchar(50) NOT NULL COMMENT '用户名',&#10;  `email` varchar(100) DEFAULT NULL COMMENT '邮箱',&#10;  `age` int(11) DEFAULT '0',&#10;  `created_at` timestamp NULL DEFAULT CURRENT_TIMESTAMP,&#10;  PRIMARY KEY (`id`)&#10;) ENGINE=InnoDB DEFAULT CHARSET=utf8mb4;"
              style="margin-top: 16px;"
            ></el-input>
            
            <div style="margin-top: 16px; text-align: right;">
              <el-button size="small" @click="sqlStatement = ''">清空</el-button>
              <el-button size="small" type="primary" @click="parseSqlStatement" :loading="parsing">
                解析 SQL
              </el-button>
            </div>
          </div>

          <!-- 字段列表表格 -->
          <el-table :data="batchFields" border style="width: 100%" v-if="!showSqlImport || batchFields.length > 0">
            <el-table-column label="字段名" width="150">
              <template slot-scope="scope">
                <el-input 
                  v-model="scope.row.name" 
                  placeholder="例如: username"
                  size="small"
                ></el-input>
              </template>
            </el-table-column>
            <el-table-column label="类型" width="140">
              <template slot-scope="scope">
                <el-select v-model="scope.row.type" placeholder="类型" size="small" style="width: 100%">
                  <el-option label="INT" value="int"></el-option>
                  <el-option label="VARCHAR" value="varchar"></el-option>
                  <el-option label="TEXT" value="text"></el-option>
                  <el-option label="DATETIME" value="datetime"></el-option>
                  <el-option label="TIMESTAMP" value="timestamp"></el-option>
                  <el-option label="BOOLEAN" value="boolean"></el-option>
                  <el-option label="DECIMAL" value="decimal"></el-option>
                </el-select>
              </template>
            </el-table-column>
            <el-table-column label="长度" width="80">
              <template slot-scope="scope">
                <el-input 
                  v-model.number="scope.row.length" 
                  placeholder="长度"
                  size="small"
                  type="number"
                  v-if="['varchar', 'int', 'decimal'].includes(scope.row.type)"
                ></el-input>
              </template>
            </el-table-column>
            <el-table-column label="可空" width="70" align="center">
              <template slot-scope="scope">
                <el-switch v-model="scope.row.nullable" size="small"></el-switch>
              </template>
            </el-table-column>
            <el-table-column label="默认值" width="100">
              <template slot-scope="scope">
                <el-input 
                  v-model="scope.row.default" 
                  placeholder="默认值"
                  size="small"
                ></el-input>
              </template>
            </el-table-column>
            <el-table-column label="备注">
              <template slot-scope="scope">
                <el-input 
                  v-model="scope.row.comment" 
                  placeholder="字段说明"
                  size="small"
                ></el-input>
              </template>
            </el-table-column>
            <el-table-column label="操作" width="80" align="center" v-if="editingFieldIndex < 0">
              <template slot-scope="scope">
                <el-button 
                  type="text" 
                  size="small" 
                  icon="el-icon-delete"
                  style="color: #F56C6C;"
                  @click="removeBatchFieldRow(scope.$index)"
                ></el-button>
              </template>
            </el-table-column>
          </el-table>

          <div v-if="batchFields.length === 0" class="empty-batch-fields">
            <p>暂无字段，点击"添加一行"开始添加</p>
          </div>
        </div>
      </div>

      <div class="drawer-footer">
        <el-button @click="batchFieldDialogVisible = false">取消</el-button>
        <el-button 
          type="primary" 
          @click="handleBatchFieldSubmit" 
          :loading="loading"
          :disabled="batchFields.length === 0"
        >
          {{ editingFieldIndex >= 0 ? '更新字段' : `添加 ${batchFields.length} 个字段` }}
        </el-button>
      </div>
    </el-drawer>

    <!-- 添加/编辑数据抽屉 -->
    <el-drawer
      :title="dataDialogMode === 'create' ? '添加记录' : '编辑记录'"
      :visible.sync="dataDialogVisible"
      direction="rtl"
      size="600px"
      :close-on-press-escape="false"
      :wrapperClosable="false"
    >
      <div class="drawer-content">
        <!-- 切换按钮 -->
        <div style="margin-bottom: 16px;" v-if="dataDialogMode === 'create'">
          <el-button 
            size="small" 
            :type="showDataSqlImport ? 'default' : 'primary'" 
            @click="showDataSqlImport = false"
          >
            手动填写
          </el-button>
          <el-button 
            size="small" 
            :type="showDataSqlImport ? 'primary' : 'default'" 
            @click="showDataSqlImport = true"
          >
            导入 SQL
          </el-button>
        </div>

        <!-- SQL 导入模式 -->
        <div v-if="showDataSqlImport && dataDialogMode === 'create'">
          <el-alert
            title="支持导入 INSERT 语句"
            type="info"
            :closable="false"
            show-icon
            style="margin-bottom: 16px;"
          >
            <div>粘贴 INSERT INTO 语句，系统会自动解析数据</div>
          </el-alert>
          
          <el-input
            v-model="dataSqlStatement"
            type="textarea"
            :rows="15"
            placeholder="粘贴 INSERT 语句，例如：&#10;&#10;INSERT INTO `users` (`username`, `email`, `age`) VALUES ('张三', 'zhangsan@example.com', 25);&#10;&#10;或批量插入：&#10;INSERT INTO `users` (`username`, `email`) VALUES &#10;('张三', 'zhangsan@example.com'),&#10;('李四', 'lisi@example.com'),&#10;('王五', 'wangwu@example.com');"
          ></el-input>
          
          <div style="margin-top: 16px; text-align: right;">
            <el-button size="small" @click="dataSqlStatement = ''">清空</el-button>
            <el-button size="small" type="primary" @click="parseDataSql" :loading="parsingDataSql">
              解析并添加
            </el-button>
          </div>
        </div>

        <!-- 手动填写模式 -->
        <el-form ref="dataForm" :model="dataForm" label-width="100px" v-else>
          <el-form-item 
            v-for="field in tableFields.filter(f => !f.isDefault)" 
            :key="field.name"
            :label="field.name"
          >
            <!-- text 类型使用 textarea -->
            <el-input 
              v-if="field.type === 'text'"
              v-model="dataForm[field.name]" 
              type="textarea"
              :rows="3"
              :placeholder="getFieldPlaceholder(field)"
            ></el-input>
            <!-- 其他类型使用普通 input -->
            <el-input 
              v-else
              v-model="dataForm[field.name]" 
              :placeholder="getFieldPlaceholder(field)"
            ></el-input>
          </el-form-item>
        </el-form>
      </div>

      <div class="drawer-footer" v-if="!showDataSqlImport || dataDialogMode === 'edit'">
        <el-button @click="dataDialogVisible = false">取消</el-button>
        <el-button type="primary" @click="handleDataSubmit" :loading="loading">
          {{ dataDialogMode === 'create' ? '添加' : '更新' }}
        </el-button>
      </div>
    </el-drawer>

    <!-- 导入数据对话框 -->
    <el-dialog
      title="导入数据"
      :visible.sync="importDialogVisible"
      width="600px"
      :close-on-click-modal="false"
    >
      <div class="import-section">
        <el-upload
          ref="upload"
          :auto-upload="false"
          :on-change="handleFileChange"
          :file-list="fileList"
          :limit="1"
          accept=".json,.csv"
          drag
        >
          <i class="el-icon-upload"></i>
          <div class="el-upload__text">将文件拖到此处，或<em>点击上传</em></div>
          <div class="el-upload__tip" slot="tip">支持 JSON 或 CSV 格式文件</div>
        </el-upload>

        <div v-if="importPreview.length > 0" class="preview-section">
          <div class="preview-header">
            <span>数据预览（前5条）</span>
            <el-tag size="small">共 {{ importPreview.length }} 条数据</el-tag>
          </div>
          <el-table :data="importPreview.slice(0, 5)" border max-height="300">
            <el-table-column 
              v-for="col in importColumns" 
              :key="col"
              :prop="col" 
              :label="col"
              show-overflow-tooltip
            ></el-table-column>
          </el-table>

          <div v-if="newFields.length > 0" class="new-fields-tip">
            <el-alert
              title="检测到新字段"
              type="info"
              :closable="false"
              show-icon
            >
              <div>以下字段将自动添加到表结构中：</div>
              <el-tag 
                v-for="field in newFields" 
                :key="field" 
                size="small" 
                type="success"
                style="margin: 4px 4px 0 0"
              >{{ field }}</el-tag>
            </el-alert>
          </div>
        </div>
      </div>

      <div slot="footer">
        <el-button @click="importDialogVisible = false">取消</el-button>
        <el-button 
          type="primary" 
          @click="handleImportSubmit" 
          :loading="loading"
          :disabled="importPreview.length === 0"
        >
          导入 {{ importPreview.length }} 条数据
        </el-button>
      </div>
    </el-dialog>
  </div>
</template>

<script>
export default {
  name: 'DatabaseManagement',
  data() {
    return {
      searchKeyword: '',
      dataSearchKeyword: '',
      tables: [],
      selectedTable: null,
      activeTab: 'data',
      tableDialogVisible: false,
      batchFieldDialogVisible: false,
      dataDialogVisible: false,
      importDialogVisible: false,
      dialogMode: 'create', // 'create' or 'edit'
      createMode: 'manual', // 'manual' or 'import'
      dataDialogMode: 'create',
      loading: false,
      editingFieldIndex: -1,
      editingDataId: null,
      editingRowData: null, // 保存编辑时的原始数据
      fileList: [],
      importPreview: [],
      importColumns: [],
      newFields: [],
      showDataSqlImport: false,
      dataSqlStatement: '',
      parsingDataSql: false,
      tableImportFileList: [],
      tableImportPreview: null,
      tableForm: {
        name: '',
        description: '',
        initFields: ['id', 'created_at', 'updated_at']
      },
      dataForm: {},
      batchFields: [],
      showSqlImport: false,
      sqlStatement: '',
      parsing: false,
      tableFields: [],
      tableData: [],
      selectedRows: [],
      // 分页相关
      dataPagination: {
        currentPage: 1,
        pageSize: 20,
        total: 0
      },
      tableRules: {
        name: [
          { required: true, message: '请输入表名', trigger: 'blur' },
          { pattern: /^[a-zA-Z_][a-zA-Z0-9_]*$/, message: '表名只能包含字母、数字和下划线，且不能以数字开头', trigger: 'blur' }
        ],
        description: [
          { required: true, message: '请输入表描述', trigger: 'blur' }
        ]
      }
    };
  },
  computed: {
    filteredTables() {
      if (!this.searchKeyword) {
        return this.tables;
      }
      return this.tables.filter(table => 
        table.name.toLowerCase().includes(this.searchKeyword.toLowerCase())
      );
    },
    filteredTableData() {
      let filtered = this.tableData;
      
      if (this.dataSearchKeyword) {
        const keyword = this.dataSearchKeyword.toLowerCase();
        filtered = this.tableData.filter(row => {
          // 在所有字段中搜索
          return Object.values(row).some(value => {
            if (value === null || value === undefined) {
              return false;
            }
            return String(value).toLowerCase().includes(keyword);
          });
        });
      }
      
      // 返回分页后的数据
      return this.paginateData(filtered);
    },
    
    // 计算总数
    filteredDataTotal() {
      if (!this.dataSearchKeyword) {
        return this.tableData.length;
      }
      
      const keyword = this.dataSearchKeyword.toLowerCase();
      return this.tableData.filter(row => {
        return Object.values(row).some(value => {
          if (value === null || value === undefined) {
            return false;
          }
          return String(value).toLowerCase().includes(keyword);
        });
      }).length;
    }
  },
  created() {
    this.fetchTables();
  },
  methods: {
    // 分页数据
    paginateData(data) {
      const start = (this.dataPagination.currentPage - 1) * this.dataPagination.pageSize;
      const end = start + this.dataPagination.pageSize;
      return data.slice(start, end);
    },
    
    // 页码改变
    handleDataPageChange(page) {
      this.dataPagination.currentPage = page;
    },
    
    // 获取字段提示信息
    getFieldTooltip(field) {
      if (field.comment) {
        return `${field.name} - ${field.comment}`;
      }
      return field.name;
    },
    
    async fetchTables() {
      const projectId = this.$route.params.id;
      try {
        const response = await this.$axios.get(`/projects/${projectId}/databases`);
        this.tables = response.data || [];
        
        // 如果有表且没有选中的，默认选中第一个
        if (this.tables.length > 0 && !this.selectedTable) {
          this.selectTable(this.tables[0]);
        }
      } catch (error) {
        this.$message.error('获取数据表列表失败');
      }
    },
    selectTable(table) {
      this.selectedTable = table;
      // 移除自动切换到数据 tab 的逻辑，保持用户当前所在的 tab
      // this.activeTab = 'data';
      this.dataSearchKeyword = ''; // 清空搜索关键词
      this.loadTableStructure(table);
      this.fetchTableData();
    },
    loadTableStructure(table) {
      // 解析表结构
      if (table.structure) {
        try {
          const structure = typeof table.structure === 'string' 
            ? JSON.parse(table.structure) 
            : table.structure;
          this.tableFields = structure.columns || structure.fields || [];
          
          console.log('原始字段列表:', this.tableFields);
          
          // 定义默认字段名称
          const defaultFieldNames = ['id', 'created_at', 'updated_at'];
          
          // 标记所有字段的 isDefault 属性
          this.tableFields = this.tableFields.map(field => ({
            ...field,
            isDefault: defaultFieldNames.includes(field.name)
          }));
          
          console.log('标记后的字段列表:', this.tableFields);
          
          // 添加默认字段（如果不存在）
          const defaultFields = [
            { name: 'id', type: 'int', length: 11, nullable: false, default: null, comment: '主键ID', isDefault: true },
            { name: 'created_at', type: 'timestamp', length: null, nullable: true, default: 'CURRENT_TIMESTAMP', comment: '创建时间', isDefault: true },
            { name: 'updated_at', type: 'timestamp', length: null, nullable: true, default: 'CURRENT_TIMESTAMP', comment: '更新时间', isDefault: true }
          ];
          
          // 检查并添加缺失的默认字段
          defaultFields.forEach(defaultField => {
            const exists = this.tableFields.some(f => f.name === defaultField.name);
            if (!exists) {
              this.tableFields.unshift(defaultField);
            }
          });
          
          console.log('最终字段列表:', this.tableFields);
          console.log('自定义字段（非默认）:', this.tableFields.filter(f => !f.isDefault));
        } catch (e) {
          console.error('解析表结构失败:', e);
          this.tableFields = [];
        }
      } else {
        // 如果没有结构，至少显示默认字段
        this.tableFields = [
          { name: 'id', type: 'int', length: 11, nullable: false, default: null, comment: '主键ID', isDefault: true },
          { name: 'created_at', type: 'timestamp', length: null, nullable: true, default: 'CURRENT_TIMESTAMP', comment: '创建时间', isDefault: true },
          { name: 'updated_at', type: 'timestamp', length: null, nullable: true, default: 'CURRENT_TIMESTAMP', comment: '更新时间', isDefault: true }
        ];
      }
    },
    async fetchTableData() {
      if (!this.selectedTable) {
        console.log('没有选中的表');
        return;
      }
      
      const projectId = this.$route.params.id;
      const databaseId = this.selectedTable.id;
      
      console.log('获取表数据:', { projectId, databaseId, tableName: this.selectedTable.name });
      
      try {
        // 先获取第一页数据，同时获取总数
        const response = await this.$axios.get(`/projects/${projectId}/databases/${databaseId}/data`, {
          params: {
            page: 1,
            page_size: 1000 // 一次获取1000条，对于大多数情况足够了
          }
        });
        console.log('API 响应:', response.data);
        
        const firstPageData = response.data.data || [];
        const total = response.data.total || 0;
        
        // 如果总数小于等于1000，直接使用第一页数据
        if (total <= 1000) {
          this.tableData = firstPageData;
          this.dataPagination.total = total;
        } else {
          // 如果总数大于1000，需要获取所有数据
          const totalPages = Math.ceil(total / 1000);
          const allData = [...firstPageData];
          
          // 并发获取剩余页面
          const promises = [];
          for (let page = 2; page <= totalPages; page++) {
            promises.push(
              this.$axios.get(`/projects/${projectId}/databases/${databaseId}/data`, {
                params: { page, page_size: 1000 }
              })
            );
          }
          
          const results = await Promise.all(promises);
          results.forEach(res => {
            allData.push(...(res.data.data || []));
          });
          
          this.tableData = allData;
          this.dataPagination.total = total;
        }
        
        console.log('表数据:', this.tableData.length, '条，总数:', this.dataPagination.total);
      } catch (error) {
        console.error('获取表数据失败:', error);
        console.error('错误详情:', error.response?.data);
        this.$message.error('获取表数据失败: ' + (error.response?.data?.error || error.message));
        this.tableData = [];
        this.dataPagination.total = 0;
      }
    },
    openCreateDialog() {
      this.dialogMode = 'create';
      this.createMode = 'manual';
      this.tableImportFileList = [];
      this.tableImportPreview = null;
      this.tableForm = {
        name: '',
        description: '',
        initFields: ['id', 'created_at', 'updated_at']
      };
      this.tableDialogVisible = true;
    },
    openEditDialog() {
      this.dialogMode = 'edit';
      this.tableForm = {
        name: this.selectedTable.name,
        description: this.selectedTable.description || '',
        initFields: []
      };
      this.tableDialogVisible = true;
    },
    async handleTableSubmit() {
      // 批量导入模式
      if (this.createMode === 'import') {
        await this.handleBatchTableImport();
        return;
      }

      // 手动创建模式
      this.$refs.tableForm.validate(async (valid) => {
        if (valid) {
          this.loading = true;
          try {
            const projectId = this.$route.params.id;
            
            // 构建初始字段
            const initFields = [];
            if (this.tableForm.initFields.includes('id')) {
              initFields.push({
                name: 'id',
                type: 'int',
                length: 11,
                nullable: false,
                default: null,
                comment: '主键ID'
              });
            }
            if (this.tableForm.initFields.includes('created_at')) {
              initFields.push({
                name: 'created_at',
                type: 'timestamp',
                length: null,
                nullable: true,
                default: 'CURRENT_TIMESTAMP',
                comment: '创建时间'
              });
            }
            if (this.tableForm.initFields.includes('updated_at')) {
              initFields.push({
                name: 'updated_at',
                type: 'timestamp',
                length: null,
                nullable: true,
                default: 'CURRENT_TIMESTAMP ON UPDATE CURRENT_TIMESTAMP',
                comment: '更新时间'
              });
            }
            
            const payload = {
              name: this.tableForm.name,
              description: this.tableForm.description
            };
            
            // 只在创建模式下包含 structure 字段
            if (this.dialogMode === 'create') {
              payload.structure = JSON.stringify({
                fields: initFields
              });
              await this.$axios.post(`/projects/${projectId}/databases`, payload);
              this.$message.success('数据表创建成功');
            } else {
              // 编辑模式下不发送 structure，避免覆盖现有结构
              await this.$axios.put(`/projects/${projectId}/databases/${this.selectedTable.id}`, payload);
              this.$message.success('数据表更新成功');
            }
            
            this.tableDialogVisible = false;
            this.fetchTables();
          } catch (error) {
            this.$message.error(this.dialogMode === 'create' ? '创建失败' : '更新失败');
          } finally {
            this.loading = false;
          }
        }
      });
    },
    async handleDelete() {
      try {
        await this.$confirm('确定要删除该数据库吗？此操作不可恢复！', '警告', {
          type: 'warning',
          confirmButtonText: '确定删除',
          cancelButtonText: '取消'
        });
        
        const projectId = this.$route.params.id;
        await this.$axios.delete(`/projects/${projectId}/databases/${this.selectedTable.id}`);
        this.$message.success('删除成功');
        this.selectedTable = null;
        this.fetchTables();
      } catch (error) {
        if (error !== 'cancel') {
          console.error('删除失败:', error);
          this.$message.error(error.response?.data?.error || '删除失败');
        }
      }
    },

    // 处理表导入文件变化
    handleTableImportFileChange(file) {
      this.tableImportFileList = [file];
      const reader = new FileReader();
      
      reader.onload = (e) => {
        try {
          const content = e.target.result;
          this.analyzeTableImportData(content);
        } catch (error) {
          console.error('文件解析失败:', error);
          this.$message.error('文件解析失败: ' + error.message);
        }
      };
      
      reader.readAsText(file.raw);
    },

    analyzeTableImportData(content) {
      try {
        let data = [];
        
        // 尝试解析为标准 JSON 数组
        try {
          data = JSON.parse(content);
          if (!Array.isArray(data)) {
            data = [data];
          }
        } catch (e) {
          // 如果失败，尝试按行解析（JSONL 格式）
          const lines = content.trim().split('\n');
          data = lines
            .filter(line => line.trim())
            .map(line => JSON.parse(line));
        }
        
        if (data.length === 0) {
          this.$message.warning('文件中没有数据');
          return;
        }
        
        // 转换 uniCloud 格式
        data = data.map(row => this.convertUniCloudFormat(row));
        
        // 分析字段结构
        const fieldMap = new Map();
        data.forEach(row => {
          Object.keys(row).forEach(key => {
            if (!fieldMap.has(key)) {
              const value = row[key];
              const fieldType = this.detectFieldType(value);
              fieldMap.set(key, fieldType);
            }
          });
        });
        
        const fields = Array.from(fieldMap.entries()).map(([name, type]) => ({
          name,
          type
        }));
        
        // 从文件名提取表名建议
        const fileName = this.tableImportFileList[0].name;
        const suggestedTableName = fileName.replace('.json', '').replace(/[^a-zA-Z0-9_]/g, '_');
        
        this.tableImportPreview = {
          tableName: suggestedTableName,
          dataCount: data.length,
          fields: fields,
          data: data
        };
        
        this.$message.success(`成功解析 ${data.length} 条数据，检测到 ${fields.length} 个字段`);
      } catch (error) {
        this.$message.error('数据解析失败: ' + error.message);
      }
    },

    detectFieldType(value) {
      if (value === null || value === undefined) {
        return 'varchar';
      }
      
      if (typeof value === 'number') {
        return Number.isInteger(value) ? 'int' : 'decimal';
      }
      
      if (typeof value === 'boolean') {
        return 'tinyint';
      }
      
      if (typeof value === 'object' || Array.isArray(value)) {
        return 'text'; // 对象和数组存储为 JSON 字符串
      }
      
      if (typeof value === 'string') {
        // 检测日期格式
        if (/^\d{4}-\d{2}-\d{2}/.test(value)) {
          return 'timestamp';
        }
        // 长文本
        if (value.length > 255) {
          return 'text';
        }
        return 'varchar';
      }
      
      return 'varchar';
    },

    async handleBatchTableImport() {
      if (!this.tableImportPreview) {
        this.$message.warning('请先上传文件');
        return;
      }
      
      if (!this.tableImportPreview.tableName) {
        this.$message.warning('请输入表名');
        return;
      }
      
      this.loading = true;
      try {
        const projectId = this.$route.params.id;
        
        // 1. 创建表结构
        const initFields = [
          {
            name: 'id',
            type: 'int',
            length: 11,
            nullable: false,
            default: null,
            comment: '主键ID'
          },
          {
            name: 'created_at',
            type: 'timestamp',
            length: null,
            nullable: true,
            default: 'CURRENT_TIMESTAMP',
            comment: '创建时间'
          },
          {
            name: 'updated_at',
            type: 'timestamp',
            length: null,
            nullable: true,
            default: 'CURRENT_TIMESTAMP ON UPDATE CURRENT_TIMESTAMP',
            comment: '更新时间'
          }
        ];
        
        // 添加检测到的字段
        this.tableImportPreview.fields.forEach(field => {
          const fieldDef = {
            name: field.name,
            type: field.type,
            length: field.type === 'varchar' ? 255 : null,
            nullable: true,
            default: null,
            comment: ''
          };
          initFields.push(fieldDef);
        });
        
        const createPayload = {
          name: this.tableImportPreview.tableName,
          description: `从 ${this.tableImportFileList[0].name} 导入`,
          structure: JSON.stringify({
            fields: initFields
          })
        };
        
        const createResponse = await this.$axios.post(`/projects/${projectId}/databases`, createPayload);
        const newTableId = createResponse.data.id;
        
        // 2. 批量导入数据
        let successCount = 0;
        let failCount = 0;
        
        for (const row of this.tableImportPreview.data) {
          try {
            const preparedData = this.prepareDataForInsert(row);
            await this.$axios.post(`/projects/${projectId}/databases/${newTableId}/data`, preparedData);
            successCount++;
          } catch (error) {
            console.error('导入单条数据失败:', error);
            failCount++;
          }
        }
        
        this.$message.success(`表创建成功！数据导入完成：成功 ${successCount} 条，失败 ${failCount} 条`);
        this.tableDialogVisible = false;
        
        // 刷新表列表
        await this.fetchTables();
        
        // 选中新创建的表
        const newTable = this.tables.find(t => t.id === newTableId);
        if (newTable) {
          this.selectTable(newTable);
        }
      } catch (error) {
        console.error('批量导入失败:', error);
        this.$message.error(error.response?.data?.error || '批量导入失败');
      } finally {
        this.loading = false;
      }
    },

    openBatchFieldDialog() {
      this.editingFieldIndex = -1; // 重置编辑索引
      this.batchFields = [
        {
          name: '',
          type: 'varchar',
          length: 255,
          nullable: true,
          default: null,
          comment: ''
        }
      ];
      this.showSqlImport = false;
      this.sqlStatement = '';
      this.batchFieldDialogVisible = true;
    },
    editFieldInBatch(field, index) {
      // 打开批量对话框，只显示当前要编辑的字段
      this.batchFields = [{ ...field }];
      this.showSqlImport = false;
      this.sqlStatement = '';
      this.editingFieldIndex = index;
      this.batchFieldDialogVisible = true;
    },
    parseSqlStatement() {
      if (!this.sqlStatement || this.sqlStatement.trim() === '') {
        this.$message.warning('请输入 SQL 语句');
        return;
      }

      this.parsing = true;
      try {
        const fields = this.extractFieldsFromSql(this.sqlStatement);
        
        if (fields.length === 0) {
          this.$message.warning('未能解析出任何字段，请检查 SQL 语句格式');
          return;
        }

        // 过滤掉默认字段
        const defaultFieldNames = ['id', 'created_at', 'updated_at'];
        const customFields = fields.filter(f => !defaultFieldNames.includes(f.name.toLowerCase()));

        if (customFields.length === 0) {
          this.$message.warning('解析出的字段都是默认字段，请添加自定义字段');
          return;
        }

        this.batchFields = customFields;
        this.showSqlImport = false;
        this.$message.success(`成功解析 ${customFields.length} 个字段`);
      } catch (error) {
        console.error('SQL 解析失败:', error);
        this.$message.error('SQL 解析失败: ' + error.message);
      } finally {
        this.parsing = false;
      }
    },
    extractFieldsFromSql(sql) {
      const fields = [];
      
      // 移除注释
      sql = sql.replace(/--[^\n]*/g, '');
      sql = sql.replace(/\/\*[\s\S]*?\*\//g, '');
      
      // 提取 CREATE TABLE 语句中的字段定义部分
      const createTableMatch = sql.match(/CREATE\s+TABLE\s+(?:IF\s+NOT\s+EXISTS\s+)?`?(\w+)`?\s*\(([\s\S]+)\)/i);
      
      if (!createTableMatch) {
        throw new Error('无法识别 CREATE TABLE 语句格式');
      }
      
      const fieldsSection = createTableMatch[2];
      
      // 分割字段定义（按逗号分割，但要注意括号内的逗号）
      const fieldLines = this.splitFieldDefinitions(fieldsSection);
      
      for (const line of fieldLines) {
        const trimmedLine = line.trim();
        
        // 跳过 PRIMARY KEY, KEY, INDEX, UNIQUE 等约束定义
        if (/^(PRIMARY\s+KEY|KEY|INDEX|UNIQUE|CONSTRAINT|FOREIGN\s+KEY)/i.test(trimmedLine)) {
          continue;
        }
        
        // 解析字段定义
        const field = this.parseFieldDefinition(trimmedLine);
        if (field) {
          fields.push(field);
        }
      }
      
      return fields;
    },
    splitFieldDefinitions(fieldsSection) {
      const lines = [];
      let currentLine = '';
      let parenthesesDepth = 0;
      
      for (let i = 0; i < fieldsSection.length; i++) {
        const char = fieldsSection[i];
        
        if (char === '(') {
          parenthesesDepth++;
        } else if (char === ')') {
          parenthesesDepth--;
        } else if (char === ',' && parenthesesDepth === 0) {
          lines.push(currentLine.trim());
          currentLine = '';
          continue;
        }
        
        currentLine += char;
      }
      
      if (currentLine.trim()) {
        lines.push(currentLine.trim());
      }
      
      return lines;
    },
    parseFieldDefinition(line) {
      // 匹配字段定义: `field_name` type(length) [NOT NULL] [DEFAULT value] [COMMENT 'comment']
      const fieldMatch = line.match(/^`?(\w+)`?\s+(\w+)(?:\(([^)]+)\))?/i);
      
      if (!fieldMatch) {
        return null;
      }
      
      const fieldName = fieldMatch[1];
      const mysqlType = fieldMatch[2].toLowerCase();
      const lengthOrPrecision = fieldMatch[3];
      
      // 转换 MySQL 类型到系统类型
      const { type, length } = this.convertMysqlType(mysqlType, lengthOrPrecision);
      
      // 检查是否可空
      const nullable = !/NOT\s+NULL/i.test(line);
      
      // 提取默认值
      let defaultValue = null;
      const defaultMatch = line.match(/DEFAULT\s+(?:'([^']*)'|"([^"]*)"|(\S+))/i);
      if (defaultMatch) {
        defaultValue = defaultMatch[1] || defaultMatch[2] || defaultMatch[3];
        if (defaultValue.toUpperCase() === 'NULL') {
          defaultValue = null;
        } else if (defaultValue.toUpperCase() === 'CURRENT_TIMESTAMP') {
          defaultValue = 'CURRENT_TIMESTAMP';
        }
      }
      
      // 提取注释
      let comment = '';
      const commentMatch = line.match(/COMMENT\s+['"]([^'"]+)['"]/i);
      if (commentMatch) {
        comment = commentMatch[1];
      }
      
      return {
        name: fieldName,
        type: type,
        length: length,
        nullable: nullable,
        default: defaultValue,
        comment: comment
      };
    },
    convertMysqlType(mysqlType, lengthOrPrecision) {
      // MySQL 类型映射到系统类型
      const typeMap = {
        // 整数类型
        'tinyint': { type: 'int', length: 4 },
        'smallint': { type: 'int', length: 6 },
        'mediumint': { type: 'int', length: 9 },
        'int': { type: 'int', length: 11 },
        'integer': { type: 'int', length: 11 },
        'bigint': { type: 'int', length: 20 },
        
        // 字符串类型
        'char': { type: 'varchar', length: 255 },
        'varchar': { type: 'varchar', length: 255 },
        'tinytext': { type: 'text', length: null },
        'text': { type: 'text', length: null },
        'mediumtext': { type: 'text', length: null },
        'longtext': { type: 'text', length: null },
        
        // 日期时间类型
        'date': { type: 'datetime', length: null },
        'datetime': { type: 'datetime', length: null },
        'timestamp': { type: 'timestamp', length: null },
        'time': { type: 'datetime', length: null },
        'year': { type: 'int', length: 4 },
        
        // 浮点数类型
        'float': { type: 'decimal', length: '10,2' },
        'double': { type: 'decimal', length: '10,2' },
        'decimal': { type: 'decimal', length: '10,2' },
        'numeric': { type: 'decimal', length: '10,2' },
        
        // 布尔类型
        'boolean': { type: 'boolean', length: null },
        'bool': { type: 'boolean', length: null },
        
        // 二进制类型（映射为文本）
        'blob': { type: 'text', length: null },
        'tinyblob': { type: 'text', length: null },
        'mediumblob': { type: 'text', length: null },
        'longblob': { type: 'text', length: null },
        'binary': { type: 'varchar', length: 255 },
        'varbinary': { type: 'varchar', length: 255 },
        
        // JSON 类型
        'json': { type: 'text', length: null },
        
        // 枚举和集合（映射为 varchar）
        'enum': { type: 'varchar', length: 50 },
        'set': { type: 'varchar', length: 255 }
      };
      
      const mapping = typeMap[mysqlType] || { type: 'varchar', length: 255 };
      
      // 如果有指定长度，使用指定的长度
      if (lengthOrPrecision) {
        // 处理 decimal(10,2) 这种格式
        if (mysqlType === 'decimal' || mysqlType === 'numeric') {
          mapping.length = lengthOrPrecision;
        } else {
          // 提取第一个数字作为长度
          const lengthMatch = lengthOrPrecision.match(/^\d+/);
          if (lengthMatch) {
            mapping.length = parseInt(lengthMatch[0]);
          }
        }
      }
      
      return mapping;
    },
    addBatchFieldRow() {
      this.batchFields.push({
        name: '',
        type: 'varchar',
        length: 255,
        nullable: true,
        default: null,
        comment: ''
      });
    },
    removeBatchFieldRow(index) {
      this.batchFields.splice(index, 1);
    },
    async handleBatchFieldSubmit() {
      // 如果是编辑模式（editingFieldIndex >= 0）
      if (this.editingFieldIndex >= 0) {
        // 编辑单个字段
        if (this.batchFields.length !== 1) {
          this.$message.warning('编辑模式下只能修改一个字段');
          return;
        }

        const field = this.batchFields[0];
        
        // 验证字段名
        if (!field.name || field.name.trim() === '') {
          this.$message.warning('请填写字段名称');
          return;
        }

        if (!/^[a-zA-Z_][a-zA-Z0-9_]*$/.test(field.name)) {
          this.$message.warning('字段名只能包含字母、数字和下划线，且不能以数字开头');
          return;
        }

        // 检查是否与其他字段重名（排除自己）
        const existingFieldNames = this.tableFields
          .map((f, idx) => idx !== this.editingFieldIndex ? f.name : null)
          .filter(name => name !== null);
        
        if (existingFieldNames.includes(field.name)) {
          this.$message.warning(`字段名 ${field.name} 已存在`);
          return;
        }

        this.loading = true;
        try {
          const projectId = this.$route.params.id;
          
          // 更新字段列表
          const fields = [...this.tableFields];
          fields[this.editingFieldIndex] = { ...field };
          
          // 过滤掉默认字段，只保存用户自定义字段
          const customFields = fields.filter(f => !f.isDefault);
          
          // 更新表结构（只发送 structure，不发送 name 避免意外覆盖）
          const payload = {
            structure: JSON.stringify({
              fields: customFields
            })
          };
          
          await this.$axios.put(`/projects/${projectId}/databases/${this.selectedTable.id}`, payload);
          this.$message.success('字段更新成功');
          
          this.batchFieldDialogVisible = false;
          this.editingFieldIndex = -1;
          
          // 保存当前选中的表ID
          const currentTableId = this.selectedTable.id;
          
          // 重新获取表列表
          await this.fetchTables();
          
          // 重新选中当前表
          const updatedTable = this.tables.find(t => t.id === currentTableId);
          if (updatedTable) {
            this.selectTable(updatedTable);
          }
        } catch (error) {
          console.error('字段更新失败:', error);
          this.$message.error('更新失败: ' + (error.response?.data?.error || error.message));
        } finally {
          this.loading = false;
        }
        return;
      }

      // 批量添加模式
      // 验证所有字段名不为空
      const emptyFields = this.batchFields.filter(f => !f.name || f.name.trim() === '');
      if (emptyFields.length > 0) {
        this.$message.warning('请填写所有字段的名称');
        return;
      }

      // 验证字段名格式
      const invalidFields = this.batchFields.filter(f => !/^[a-zA-Z_][a-zA-Z0-9_]*$/.test(f.name));
      if (invalidFields.length > 0) {
        this.$message.warning('字段名只能包含字母、数字和下划线，且不能以数字开头');
        return;
      }

      // 检查重复字段名
      const fieldNames = this.batchFields.map(f => f.name);
      const duplicates = fieldNames.filter((name, index) => fieldNames.indexOf(name) !== index);
      if (duplicates.length > 0) {
        this.$message.warning(`存在重复的字段名: ${duplicates.join(', ')}`);
        return;
      }

      // 检查与现有字段冲突
      const existingFieldNames = this.tableFields.map(f => f.name);
      const conflicts = fieldNames.filter(name => existingFieldNames.includes(name));
      if (conflicts.length > 0) {
        this.$message.warning(`以下字段已存在: ${conflicts.join(', ')}`);
        return;
      }

      this.loading = true;
      try {
        const projectId = this.$route.params.id;
        
        // 获取当前自定义字段
        const customFields = this.tableFields.filter(f => !f.isDefault);
        
        // 添加所有新字段
        this.batchFields.forEach(field => {
          customFields.push({ ...field });
        });
        
        // 更新表结构（只发送 structure，不发送 name 避免意外覆盖）
        const payload = {
          structure: JSON.stringify({
            fields: customFields
          })
        };
        
        await this.$axios.put(`/projects/${projectId}/databases/${this.selectedTable.id}`, payload);
        this.$message.success(`成功添加 ${this.batchFields.length} 个字段`);
        
        this.batchFieldDialogVisible = false;
        
        // 保存当前选中的表ID
        const currentTableId = this.selectedTable.id;
        
        // 重新获取表列表
        await this.fetchTables();
        
        // 重新选中当前表
        const updatedTable = this.tables.find(t => t.id === currentTableId);
        if (updatedTable) {
          this.selectTable(updatedTable);
        }
      } catch (error) {
        console.error('批量添加字段失败:', error);
        this.$message.error('批量添加失败: ' + (error.response?.data?.error || error.message));
      } finally {
        this.loading = false;
      }
    },
    async deleteField(index) {
      // 检查是否是默认字段
      const field = this.tableFields[index];
      if (field && field.isDefault) {
        this.$message.warning('默认字段不允许删除');
        return;
      }
      
      try {
        await this.$confirm('确定要删除该字段吗？', '提示', {
          type: 'warning'
        });
        
        const projectId = this.$route.params.id;
        const fields = [...this.tableFields];
        fields.splice(index, 1);
        
        // 过滤掉默认字段，只保存用户自定义字段
        const customFields = fields.filter(f => !f.isDefault);
        
        // 更新表结构（只发送 structure，不发送 name 避免意外覆盖）
        const payload = {
          structure: JSON.stringify({
            fields: customFields
          })
        };
        
        await this.$axios.put(`/projects/${projectId}/databases/${this.selectedTable.id}`, payload);
        this.$message.success('字段删除成功');
        
        // 保存当前选中的表ID
        const currentTableId = this.selectedTable.id;
        
        // 重新获取表列表
        await this.fetchTables();
        
        // 重新选中当前表
        const updatedTable = this.tables.find(t => t.id === currentTableId);
        if (updatedTable) {
          this.selectTable(updatedTable);
        }
      } catch (error) {
        if (error !== 'cancel') {
          this.$message.error('删除失败');
        }
      }
    },
    
    // 数据管理方法
    openAddDataDialog() {
      this.dataDialogMode = 'create';
      this.editingDataId = null;
      this.showDataSqlImport = false;
      this.dataSqlStatement = '';
      // 只初始化自定义字段（非默认字段）
      const formData = {};
      this.tableFields.filter(f => !f.isDefault).forEach(field => {
        this.$set(formData, field.name, '');
      });
      this.dataForm = formData;
      console.log('打开添加对话框，表单字段:', this.tableFields.filter(f => !f.isDefault));
      console.log('初始化表单数据:', this.dataForm);
      this.dataDialogVisible = true;
    },
    
    editData(row) {
      this.dataDialogMode = 'edit';
      this.editingDataId = row.id;
      this.editingRowData = row; // 保存原始数据用于 placeholder
      // 只复制自定义字段的值
      const formData = {};
      this.tableFields.filter(f => !f.isDefault).forEach(field => {
        this.$set(formData, field.name, row[field.name] || '');
      });
      this.dataForm = formData;
      console.log('编辑数据，表单字段:', this.tableFields.filter(f => !f.isDefault));
      console.log('表单数据:', this.dataForm);
      this.dataDialogVisible = true;
    },
    
    // 获取字段的 placeholder
    getFieldPlaceholder(field) {
      // 编辑模式下，显示更新时间（如果有）
      if (this.dataDialogMode === 'edit' && this.editingRowData) {
        const updatedAt = this.editingRowData.updated_at;
        if (updatedAt) {
          return `请输入${field.name}（更新时间: ${updatedAt}）`;
        }
      }
      
      // 根据字段类型提供不同的提示
      if (field.type === 'text') {
        return `请输入${field.name}（支持多行文本）`;
      } else if (field.type === 'int') {
        return `请输入${field.name}（整数）`;
      } else if (field.type === 'decimal') {
        return `请输入${field.name}（小数）`;
      } else if (field.type === 'datetime' || field.type === 'timestamp') {
        return `请输入${field.name}（格式: YYYY-MM-DD HH:mm:ss）`;
      } else if (field.type === 'boolean') {
        return `请输入${field.name}（true/false 或 1/0）`;
      }
      
      return `请输入${field.name}`;
    },
    
    async handleDataSubmit() {
      this.loading = true;
      try {
        const projectId = this.$route.params.id;
        const databaseId = this.selectedTable.id;
        
        if (this.dataDialogMode === 'create') {
          await this.$axios.post(`/projects/${projectId}/databases/${databaseId}/data`, this.dataForm);
          this.$message.success('记录添加成功');
        } else {
          await this.$axios.put(`/projects/${projectId}/databases/${databaseId}/data/${this.editingDataId}`, this.dataForm);
          this.$message.success('记录更新成功');
        }
        
        this.dataDialogVisible = false;
        this.fetchTableData();
      } catch (error) {
        console.error('操作失败:', error);
        this.$message.error('操作失败: ' + (error.response?.data?.error || error.message));
      } finally {
        this.loading = false;
      }
    },
    
    async deleteData(id) {
      try {
        await this.$confirm('确定要删除该记录吗？', '提示', {
          type: 'warning'
        });
        
        const projectId = this.$route.params.id;
        const databaseId = this.selectedTable.id;
        
        await this.$axios.delete(`/projects/${projectId}/databases/${databaseId}/data/${id}`);
        this.$message.success('记录删除成功');
        this.fetchTableData();
      } catch (error) {
        if (error !== 'cancel') {
          console.error('删除失败:', error);
          this.$message.error('删除失败: ' + (error.response?.data?.error || error.message));
        }
      }
    },

    // 解析 INSERT SQL 语句
    async parseDataSql() {
      if (!this.dataSqlStatement || this.dataSqlStatement.trim() === '') {
        this.$message.warning('请输入 SQL 语句');
        return;
      }

      this.parsingDataSql = true;
      try {
        const records = this.extractDataFromInsertSql(this.dataSqlStatement);
        
        if (records.length === 0) {
          this.$message.warning('未能解析出任何数据，请检查 SQL 语句格式');
          return;
        }

        // 批量添加数据（使用批量创建接口）
        const projectId = this.$route.params.id;
        const databaseId = this.selectedTable.id;
        
        try {
          const response = await this.$axios.post(
            `/projects/${projectId}/databases/${databaseId}/data/batch-create`,
            { records }
          );
          
          const { created_count, failed_count } = response.data;
          
          if (created_count > 0) {
            this.$message.success(`成功添加 ${created_count} 条记录${failed_count > 0 ? `，失败 ${failed_count} 条` : ''}`);
            this.dataDialogVisible = false;
            this.dataSqlStatement = '';
            this.fetchTableData();
          } else {
            this.$message.error('所有记录添加失败');
          }
        } catch (error) {
          console.error('批量添加失败:', error);
          this.$message.error('批量添加失败: ' + (error.response?.data?.error || error.message));
        }
      } catch (error) {
        console.error('SQL 解析失败:', error);
        this.$message.error('SQL 解析失败: ' + error.message);
      } finally {
        this.parsingDataSql = false;
      }
    },

    // 从 INSERT SQL 语句中提取数据
    extractDataFromInsertSql(sql) {
      const records = [];
      
      try {
        // 移除注释和多余空格
        sql = sql.replace(/--.*$/gm, '').replace(/\/\*[\s\S]*?\*\//g, '').trim();
        
        // 匹配 INSERT INTO 语句
        const insertRegex = /INSERT\s+INTO\s+`?(\w+)`?\s*\((.*?)\)\s*VALUES\s*(.*?)(?:;|$)/gis;
        const match = insertRegex.exec(sql);
        
        if (!match) {
          throw new Error('无法识别 INSERT 语句格式');
        }
        
        // 提取字段名
        const fieldsStr = match[2];
        const fields = fieldsStr.split(',').map(f => f.trim().replace(/`/g, ''));
        
        // 提取值
        const valuesStr = match[3];
        
        // 匹配所有的值组 (value1, value2, ...)
        const valueGroupRegex = /\(((?:[^()]*|\([^)]*\))*)\)/g;
        let valueMatch;
        
        while ((valueMatch = valueGroupRegex.exec(valuesStr)) !== null) {
          const valuesGroup = valueMatch[1];
          const values = this.parseValueGroup(valuesGroup);
          
          if (values.length !== fields.length) {
            console.warn('字段数量与值数量不匹配，跳过此行');
            continue;
          }
          
          const record = {};
          fields.forEach((field, index) => {
            record[field] = values[index];
          });
          
          records.push(record);
        }
        
        return records;
      } catch (error) {
        console.error('SQL 解析错误:', error);
        throw error;
      }
    },

    // 解析值组
    parseValueGroup(valuesStr) {
      const values = [];
      let current = '';
      let inString = false;
      let stringChar = '';
      let escaped = false;
      
      for (let i = 0; i < valuesStr.length; i++) {
        const char = valuesStr[i];
        
        if (escaped) {
          current += char;
          escaped = false;
          continue;
        }
        
        if (char === '\\') {
          escaped = true;
          continue;
        }
        
        if ((char === '"' || char === "'") && !inString) {
          inString = true;
          stringChar = char;
          continue;
        }
        
        if (char === stringChar && inString) {
          inString = false;
          stringChar = '';
          continue;
        }
        
        if (char === ',' && !inString) {
          values.push(this.parseValue(current.trim()));
          current = '';
          continue;
        }
        
        current += char;
      }
      
      if (current.trim()) {
        values.push(this.parseValue(current.trim()));
      }
      
      return values;
    },

    // 解析单个值
    parseValue(value) {
      value = value.trim();
      
      // NULL
      if (value.toUpperCase() === 'NULL') {
        return null;
      }
      
      // 数字
      if (/^-?\d+(\.\d+)?$/.test(value)) {
        return value.includes('.') ? parseFloat(value) : parseInt(value);
      }
      
      // 布尔值
      if (value.toLowerCase() === 'true') return true;
      if (value.toLowerCase() === 'false') return false;
      
      // 字符串（已经去掉引号了）
      return value;
    },

    // 导入数据方法
    openImportDialog() {
      this.importDialogVisible = true;
      this.fileList = [];
      this.importPreview = [];
      this.importColumns = [];
      this.newFields = [];
    },

    handleFileChange(file) {
      this.fileList = [file];
      const reader = new FileReader();
      
      reader.onload = (e) => {
        try {
          const content = e.target.result;
          const fileName = file.name.toLowerCase();
          
          if (fileName.endsWith('.json')) {
            this.parseJsonFile(content);
          } else if (fileName.endsWith('.csv')) {
            this.parseCsvFile(content);
          } else {
            this.$message.error('不支持的文件格式');
          }
        } catch (error) {
          console.error('文件解析失败:', error);
          this.$message.error('文件解析失败: ' + error.message);
        }
      };
      
      reader.readAsText(file.raw);
    },

    parseJsonFile(content) {
      try {
        let data = [];
        
        // 尝试解析为标准 JSON 数组
        try {
          data = JSON.parse(content);
          
          // 如果是单个对象，转换为数组
          if (!Array.isArray(data)) {
            data = [data];
          }
        } catch (e) {
          // 如果失败，尝试按行解析（JSONL 格式，uniCloud 导出格式）
          const lines = content.trim().split('\n');
          data = lines
            .filter(line => line.trim())
            .map(line => JSON.parse(line));
        }
        
        if (data.length === 0) {
          this.$message.warning('文件中没有数据');
          return;
        }
        
        // 转换 uniCloud 特殊字段格式
        data = data.map(row => this.convertUniCloudFormat(row));
        
        this.importPreview = data;
        this.analyzeImportData(data);
        this.$message.success(`成功解析 ${data.length} 条数据`);
      } catch (error) {
        throw new Error('JSON 格式错误: ' + error.message);
      }
    },
    
    convertUniCloudFormat(obj) {
      // 递归转换 uniCloud 特殊格式
      if (obj === null || obj === undefined) {
        return obj;
      }
      
      // 处理数组
      if (Array.isArray(obj)) {
        return obj.map(item => this.convertUniCloudFormat(item));
      }
      
      // 处理对象
      if (typeof obj === 'object') {
        // 处理 MongoDB ObjectId: {"$oid": "xxx"} -> "xxx"
        if (obj.$oid) {
          return obj.$oid;
        }
        
        // 处理 MongoDB Date: {"$date": "xxx"} -> "xxx"
        if (obj.$date) {
          return obj.$date;
        }
        
        // 处理 MongoDB NumberLong: {"$numberLong": "xxx"} -> xxx
        if (obj.$numberLong) {
          return parseInt(obj.$numberLong);
        }
        
        // 处理 MongoDB NumberDecimal: {"$numberDecimal": "xxx"} -> xxx
        if (obj.$numberDecimal) {
          return parseFloat(obj.$numberDecimal);
        }
        
        // 递归处理对象的所有属性
        const result = {};
        for (const key in obj) {
          // 保留 _id 字段（如果是 MongoDB ObjectId 格式，已在上面转换）
          result[key] = this.convertUniCloudFormat(obj[key]);
        }
        return result;
      }
      
      return obj;
    },
    
    // 将对象/数组字段转换为 JSON 字符串（用于 text 类型字段）
    prepareDataForInsert(row) {
      const prepared = {};
      
      for (const key in row) {
        const value = row[key];
        
        // 如果值是对象或数组，转换为 JSON 字符串
        if (value !== null && typeof value === 'object') {
          prepared[key] = JSON.stringify(value);
        } else {
          prepared[key] = value;
        }
      }
      
      return prepared;
    },

    parseCsvFile(content) {
      try {
        const lines = content.trim().split('\n');
        if (lines.length < 2) {
          this.$message.warning('CSV 文件至少需要包含标题行和一行数据');
          return;
        }
        
        // 解析标题行
        const headers = this.parseCsvLine(lines[0]);
        
        // 解析数据行
        const data = [];
        for (let i = 1; i < lines.length; i++) {
          if (lines[i].trim()) {
            const values = this.parseCsvLine(lines[i]);
            const row = {};
            headers.forEach((header, index) => {
              row[header] = values[index] || '';
            });
            data.push(row);
          }
        }
        
        this.importPreview = data;
        this.analyzeImportData(data);
        this.$message.success(`成功解析 ${data.length} 条数据`);
      } catch (error) {
        throw new Error('CSV 格式错误');
      }
    },

    parseCsvLine(line) {
      const result = [];
      let current = '';
      let inQuotes = false;
      
      for (let i = 0; i < line.length; i++) {
        const char = line[i];
        
        if (char === '"') {
          inQuotes = !inQuotes;
        } else if (char === ',' && !inQuotes) {
          result.push(current.trim());
          current = '';
        } else {
          current += char;
        }
      }
      
      result.push(current.trim());
      return result;
    },

    analyzeImportData(data) {
      if (data.length === 0) return;
      
      // 获取所有列名
      const allColumns = new Set();
      data.forEach(row => {
        Object.keys(row).forEach(key => allColumns.add(key));
      });
      
      this.importColumns = Array.from(allColumns);
      
      // 检测新字段（排除默认字段）
      const existingFields = this.tableFields.map(f => f.name);
      const defaultFields = ['id', 'created_at', 'updated_at'];
      
      this.newFields = this.importColumns.filter(col => 
        !existingFields.includes(col) && !defaultFields.includes(col)
      );
    },

    async handleImportSubmit() {
      if (this.importPreview.length === 0) {
        this.$message.warning('没有可导入的数据');
        return;
      }
      
      this.loading = true;
      try {
        const projectId = this.$route.params.id;
        const databaseId = this.selectedTable.id;
        
        // 如果有新字段，先更新表结构
        if (this.newFields.length > 0) {
          await this.addNewFields();
        }
        
        // 准备批量导入的数据
        const records = this.importPreview.map(row => {
          // 过滤掉默认字段
          const dataToImport = {};
          Object.keys(row).forEach(key => {
            if (!['id', 'created_at', 'updated_at'].includes(key)) {
              dataToImport[key] = row[key];
            }
          });
          
          // 将对象/数组字段转换为 JSON 字符串
          return this.prepareDataForInsert(dataToImport);
        });
        
        // 使用批量创建接口
        const response = await this.$axios.post(
          `/projects/${projectId}/databases/${databaseId}/data/batch-create`,
          { records }
        );
        
        const { created_count, failed_count } = response.data;
        this.$message.success(`导入完成！成功 ${created_count} 条，失败 ${failed_count} 条`);
        this.importDialogVisible = false;
        
        // 刷新数据和表结构
        await this.fetchTables();
        const updatedTable = this.tables.find(t => t.id === databaseId);
        if (updatedTable) {
          this.selectTable(updatedTable);
        }
      } catch (error) {
        console.error('导入失败:', error);
        this.$message.error('导入失败: ' + (error.response?.data?.error || error.message));
      } finally {
        this.loading = false;
      }
    },

    async addNewFields() {
      const projectId = this.$route.params.id;
      const databaseId = this.selectedTable.id;
      
      // 获取当前自定义字段
      const customFields = this.tableFields.filter(f => !f.isDefault);
      
      // 添加新字段（默认为 varchar 类型）
      this.newFields.forEach(fieldName => {
        customFields.push({
          name: fieldName,
          type: 'varchar',
          length: 255,
          nullable: true,
          default: null,
          comment: '自动导入字段'
        });
      });
      
      // 更新表结构（只发送 structure，不发送 name 避免意外覆盖）
      const payload = {
        structure: JSON.stringify({
          fields: customFields
        })
      };
      
      await this.$axios.put(`/projects/${projectId}/databases/${databaseId}`, payload);
    },

    // 统一处理导入导出命令
    handleImportExport(command) {
      if (command === 'import') {
        this.openImportDialog();
      } else if (command === 'export-json') {
        this.handleExport('json');
      } else if (command === 'export-csv') {
        this.handleExport('csv');
      }
    },

    // 导出数据方法
    handleExport(format) {
      if (!this.tableData || this.tableData.length === 0) {
        this.$message.warning('没有可导出的数据');
        return;
      }

      try {
        if (format === 'json') {
          this.exportAsJson();
        } else if (format === 'csv') {
          this.exportAsCsv();
        }
        this.$message.success('导出成功');
      } catch (error) {
        console.error('导出失败:', error);
        this.$message.error('导出失败: ' + error.message);
      }
    },

    exportAsJson() {
      const jsonStr = JSON.stringify(this.tableData, null, 2);
      const blob = new Blob([jsonStr], { type: 'application/json' });
      this.downloadFile(blob, `${this.selectedTable.name}_${this.getTimestamp()}.json`);
    },

    exportAsCsv() {
      // 获取所有列名
      const columns = this.tableFields.map(f => f.name);
      
      // 构建 CSV 内容
      let csvContent = '';
      
      // 添加标题行
      csvContent += columns.map(col => this.escapeCsvValue(col)).join(',') + '\n';
      
      // 添加数据行
      this.tableData.forEach(row => {
        const values = columns.map(col => {
          const value = row[col];
          return this.escapeCsvValue(value);
        });
        csvContent += values.join(',') + '\n';
      });
      
      // 使用 UTF-8 BOM 以确保 Excel 正确识别中文
      const BOM = '\uFEFF';
      const blob = new Blob([BOM + csvContent], { type: 'text/csv;charset=utf-8;' });
      this.downloadFile(blob, `${this.selectedTable.name}_${this.getTimestamp()}.csv`);
    },

    escapeCsvValue(value) {
      if (value === null || value === undefined) {
        return '';
      }
      
      const str = String(value);
      
      // 如果包含逗号、引号或换行符，需要用引号包裹并转义引号
      if (str.includes(',') || str.includes('"') || str.includes('\n')) {
        return '"' + str.replace(/"/g, '""') + '"';
      }
      
      return str;
    },

    downloadFile(blob, filename) {
      const link = document.createElement('a');
      const url = URL.createObjectURL(blob);
      
      link.href = url;
      link.download = filename;
      link.style.display = 'none';
      
      document.body.appendChild(link);
      link.click();
      
      // 清理
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    },

    getTimestamp() {
      const now = new Date();
      const year = now.getFullYear();
      const month = String(now.getMonth() + 1).padStart(2, '0');
      const day = String(now.getDate()).padStart(2, '0');
      const hours = String(now.getHours()).padStart(2, '0');
      const minutes = String(now.getMinutes()).padStart(2, '0');
      const seconds = String(now.getSeconds()).padStart(2, '0');
      
      return `${year}${month}${day}_${hours}${minutes}${seconds}`;
    },
    
    // 导出表结构为 SQL
    exportTableStructure() {
      if (!this.selectedTable || !this.tableFields || this.tableFields.length === 0) {
        this.$message.warning('没有可导出的表结构');
        return;
      }

      try {
        const sql = this.generateCreateTableSQL();
        const blob = new Blob([sql], { type: 'text/plain;charset=utf-8;' });
        this.downloadFile(blob, `${this.selectedTable.name}_structure_${this.getTimestamp()}.sql`);
        this.$message.success('表结构导出成功');
      } catch (error) {
        console.error('导出表结构失败:', error);
        this.$message.error('导出失败: ' + error.message);
      }
    },
    
    // 生成 CREATE TABLE SQL 语句
    generateCreateTableSQL() {
      const tableName = this.selectedTable.name;
      const fields = this.tableFields;
      
      let sql = `CREATE TABLE \`${tableName}\` (\n`;
      
      // 生成字段定义
      const fieldDefinitions = fields.map(field => {
        let def = `  \`${field.name}\``;
        
        // 类型和长度
        if (field.type === 'varchar' || field.type === 'int') {
          def += ` ${field.type.toUpperCase()}(${field.length || (field.type === 'varchar' ? 255 : 11)})`;
        } else if (field.type === 'decimal') {
          def += ` DECIMAL(${field.length || '10,2'})`;
        } else if (field.type === 'text') {
          def += ` TEXT`;
        } else if (field.type === 'datetime') {
          def += ` DATETIME`;
        } else if (field.type === 'timestamp') {
          def += ` TIMESTAMP`;
        } else if (field.type === 'boolean') {
          def += ` TINYINT(1)`;
        } else {
          def += ` ${field.type.toUpperCase()}`;
        }
        
        // 是否可空
        if (!field.nullable) {
          def += ' NOT NULL';
        } else {
          def += ' NULL';
        }
        
        // 默认值
        if (field.default !== null && field.default !== undefined && field.default !== '') {
          if (field.default === 'CURRENT_TIMESTAMP') {
            def += ' DEFAULT CURRENT_TIMESTAMP';
          } else if (field.type === 'int' || field.type === 'decimal' || field.type === 'boolean') {
            def += ` DEFAULT ${field.default}`;
          } else {
            def += ` DEFAULT '${field.default}'`;
          }
        }
        
        // 注释
        if (field.comment) {
          def += ` COMMENT '${field.comment}'`;
        }
        
        return def;
      });
      
      sql += fieldDefinitions.join(',\n');
      
      // 添加主键
      const idField = fields.find(f => f.name === 'id');
      if (idField) {
        sql += ',\n  PRIMARY KEY (`id`)';
      }
      
      sql += '\n) ENGINE=InnoDB DEFAULT CHARSET=utf8mb4 COMMENT=\'' + (this.selectedTable.description || tableName) + '\';';
      
      return sql;
    },
    
    // 处理表格多选
    handleSelectionChange(selection) {
      this.selectedRows = selection;
    },
    
    // 批量删除数据
    async batchDeleteData() {
      if (this.selectedRows.length === 0) {
        this.$message.warning('请先选择要删除的数据');
        return;
      }

      try {
        await this.$confirm(`确定要删除选中的 ${this.selectedRows.length} 条记录吗？此操作不可恢复！`, '警告', {
          type: 'warning',
          confirmButtonText: '确定删除',
          cancelButtonText: '取消'
        });
        
        const projectId = this.$route.params.id;
        const databaseId = this.selectedTable.id;
        const ids = this.selectedRows.map(row => row.id);
        
        // 批量删除
        await this.$axios.post(`/projects/${projectId}/databases/${databaseId}/data/batch-delete`, {
          ids: ids
        });
        
        this.$message.success(`成功删除 ${ids.length} 条记录`);
        this.selectedRows = [];
        this.fetchTableData();
      } catch (error) {
        if (error !== 'cancel') {
          console.error('批量删除失败:', error);
          this.$message.error('批量删除失败: ' + (error.response?.data?.error || error.message));
        }
      }
    }
  }
};
</script>

<style scoped>
.databases-container {
  display: flex;
  height: calc(100vh - 60px);
  background: #f5f7fa;
}

/* 创建模式切换 */
.create-mode-switch {
  margin-bottom: 20px;
  text-align: center;
}

/* 批量导入区域 */
.batch-import-section {
  padding: 10px 0;
}

.import-preview {
  max-height: 300px;
  overflow-y: auto;
}

/* 左侧边栏 */
.database-sidebar {
  width: 260px;
  background: white;
  border-right: 1px solid #e4e7ed;
  display: flex;
  flex-direction: column;
}

.sidebar-header {
  padding: 16px;
  border-bottom: 1px solid #e4e7ed;
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.sidebar-header h3 {
  margin: 0;
  font-size: 16px;
  font-weight: 500;
  color: #303133;
}

.header-actions {
  display: flex;
  gap: 8px;
}

.search-input {
  margin: 12px 16px;
  width: auto;
}

.table-list {
  flex: 1;
  overflow-y: auto;
  padding: 8px;
}

.table-item {
  padding: 12px;
  margin-bottom: 4px;
  border-radius: 4px;
  cursor: pointer;
  display: flex;
  align-items: flex-start;
  gap: 10px;
  transition: all 0.3s;
  border: 1px solid transparent;
}

.table-item:hover {
  background: #f5f7fa;
  border-color: #e4e7ed;
}

.table-item.active {
  background: #ecf5ff;
  border-color: #409eff;
}

.table-item i {
  font-size: 18px;
  color: #409eff;
  margin-top: 2px;
}

.table-info {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 4px;
  min-width: 0;
}

.table-name {
  font-size: 14px;
  font-weight: 500;
  color: #303133;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.table-desc {
  font-size: 12px;
  color: #909399;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.empty-list {
  text-align: center;
  padding: 40px 20px;
  color: #909399;
  font-size: 14px;
}

.sidebar-footer {
  padding: 12px;
  border-top: 1px solid #e4e7ed;
  display: flex;
  flex-direction: column;
  gap: 4px;
}

/* 右侧内容区 */
.table-content {
  flex: 1;
  overflow: hidden; /* 改为 hidden，不要滚动条 */
  background: white;
  display: flex;
  flex-direction: column;
}

.empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 100%;
  color: #909399;
}

.empty-state i {
  font-size: 64px;
  color: #dcdfe6;
  margin-bottom: 16px;
}

.empty-state p {
  margin-bottom: 20px;
  font-size: 14px;
}

.table-detail {
  height: 100%;
  display: flex;
  flex-direction: column;
  overflow: hidden; /* 确保不出现滚动条 */
}

.detail-header {
  padding: 12px 20px; /* 减小上下 padding */
  border-bottom: 1px solid #e4e7ed;
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-shrink: 0; /* 不收缩 */
}

.header-left {
  display: flex;
  align-items: center;
  gap: 12px;
}

.header-left h2 {
  margin: 0;
  font-size: 18px; /* 稍微减小字体 */
  font-weight: 500;
  color: #303133;
}

.table-desc-text {
  font-size: 13px; /* 稍微减小字体 */
  color: #909399;
}

.table-tabs {
  flex: 1;
  padding: 12px 20px; /* 减小 padding */
  overflow: hidden;
  display: flex;
  flex-direction: column;
}

.table-tabs >>> .el-tabs__header {
  flex-shrink: 0; /* 不收缩 */
  margin-bottom: 12px; /* 减小间距 */
}

.table-tabs >>> .el-tabs__content {
  flex: 1;
  overflow: hidden;
  height: 100%;
}

.table-tabs >>> .el-tab-pane {
  height: 100%;
  overflow: hidden;
  display: flex;
  flex-direction: column;
}

.structure-section,
.index-section {
  height: 100%;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.structure-section .section-header,
.index-section .section-header {
  flex-shrink: 0;
  margin-bottom: 16px;
}

.structure-section .table-container,
.index-section .table-container {
  flex: 1;
  overflow: hidden;
}

.data-section {
  height: 100%;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.data-section .data-toolbar {
  flex-shrink: 0;
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 12px;
}

.data-section .data-toolbar .search-tip {
  color: #909399;
  font-size: 12px;
  white-space: nowrap;
}

.data-section .data-toolbar .action-buttons {
  display: flex;
  gap: 8px;
}

.section-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 12px; /* 减小间距 */
  font-size: 14px;
  font-weight: 500;
  color: #303133;
  flex-shrink: 0; /* 不收缩 */
}

.section-header .action-buttons {
  display: flex;
  gap: 8px;
}

.empty-data {
  text-align: center;
  padding: 60px 20px;
  color: #909399;
  background: #fafafa;
  border: 1px dashed #e4e7ed;
  border-radius: 4px;
  margin-top: 16px;
}

.pagination-container {
  display: flex;
  justify-content: flex-end;
  padding: 20px 0;
  margin-top: 16px;
}

.data-section .section-header,
.data-section .search-bar {
  flex-shrink: 0;
}

.data-section .table-wrapper {
  flex: 1;
  overflow: hidden; /* 改为 hidden，表格内部自己滚动 */
  margin-top: 12px; /* 减小间距 */
  display: flex;
  flex-direction: column;
}

.data-section .pagination-footer {
  flex-shrink: 0;
  display: flex;
  justify-content: flex-end;
  align-items: center;
  padding: 12px 0; /* 减小 padding */
  border-top: 1px solid #ebeef5;
  background: #fff;
  gap: 16px;
}

.data-section .pagination-footer .footer-left {
  margin-right: auto;
}

.tip {
  color: #909399;
  font-size: 14px;
  margin-bottom: 16px;
}

.form-tip {
  display: block;
  margin-top: 8px;
  font-size: 12px;
  color: #909399;
  line-height: 1.5;
}

/* 抽屉样式 */
.drawer-content {
  padding: 20px;
  height: calc(100% - 70px);
  overflow-y: auto;
}

.drawer-footer {
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  padding: 16px 20px;
  border-top: 1px solid #e4e7ed;
  background: white;
  text-align: right;
}

/* 导入数据样式 */
.import-section {
  padding: 20px 0;
}

.preview-section {
  margin-top: 24px;
}

.preview-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 12px;
  font-size: 14px;
  font-weight: 500;
  color: #303133;
}

.new-fields-tip {
  margin-top: 16px;
}

.new-fields-tip .el-alert {
  padding: 12px;
}

/* 数据搜索栏 */
.search-bar {
  display: flex;
  align-items: center;
  gap: 16px;
  margin-bottom: 16px;
  padding: 12px 0;
}

.search-tip {
  font-size: 13px;
  color: #606266;
}

/* 抽屉样式 */
.drawer-content {
  padding: 20px;
  height: calc(100% - 70px);
  overflow-y: auto;
}

.drawer-footer {
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  padding: 16px 20px;
  border-top: 1px solid #e4e7ed;
  background: white;
  text-align: right;
}

/* 批量添加字段样式 */
.batch-field-section {
  padding: 0;
}

.batch-field-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 16px;
  font-size: 14px;
  font-weight: 500;
  color: #303133;
}

.batch-field-header > div {
  display: flex;
  gap: 8px;
}

.sql-import-section {
  margin-bottom: 20px;
  padding: 16px;
  background: #f5f7fa;
  border-radius: 4px;
}

.sql-import-section .el-alert {
  margin-bottom: 0;
}

.empty-batch-fields {
  text-align: center;
  padding: 40px 20px;
  color: #909399;
  background: #fafafa;
  border: 1px dashed #e4e7ed;
  border-radius: 4px;
  margin-top: 16px;
}

.empty-batch-fields p {
  margin: 0;
  font-size: 14px;
}
</style>
